import React from 'react';
import {
  FunnelChart as RechartsFunnelChart,
  Funnel,
  Cell,
  LabelList,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { WidgetHeaderIcon } from '@/shared/components/ui/WidgetHeaderIcon';
import type { FunnelAnalyticsResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
interface FunnelChartProps {
  data: FunnelAnalyticsResponse;
}

// Purple gradient colors for funnel steps (darker → lighter)
const FUNNEL_COLORS = [
  '#7c3aed',
  '#8b5cf6',
  '#a78bfa',
  '#c4b5fd',
  '#ddd6fe',
  '#ede9fe',
  '#f5f3ff',
];

/**
 * Custom tooltip (always-light per project standard)
 */
const CustomFunnelTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;
  const entry = payload[0].payload;

  const formatDuration = (days: number): string => {
    if (days < 1) return `${Math.round(days * 24)}h`;
    return `${days.toFixed(1)}d`;
  };

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px 16px',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
        minWidth: '180px',
      }}
    >
      <p style={{ color: '#1e293b', fontWeight: 600, fontSize: '13px', marginBottom: '8px' }}>
        {entry.name}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
          <span style={{ color: '#64748b', fontSize: '12px' }}>Acumulado</span>
          <span style={{ color: '#1e293b', fontSize: '12px', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
            {entry.value}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
          <span style={{ color: '#64748b', fontSize: '12px' }}>Nesta etapa</span>
          <span style={{ color: '#1e293b', fontSize: '12px', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
            {entry.originalValue}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
          <span style={{ color: '#64748b', fontSize: '12px' }}>Ativos</span>
          <span style={{ color: '#1e293b', fontSize: '12px', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
            {entry.leadsInProgress}
          </span>
        </div>
        {entry.conversionRate != null && (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#64748b', fontSize: '12px' }}>Conversão</span>
            <span style={{ color: '#10b981', fontSize: '12px', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
              {entry.conversionRate.toFixed(1)}%
            </span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
          <span style={{ color: '#64748b', fontSize: '12px' }}>Tempo médio</span>
          <span style={{ color: '#1e293b', fontSize: '12px', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
            {formatDuration(entry.avgDurationDays)}
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Visualização de funil com gráfico tipo funil real (recharts)
 *
 * Substitui as barras de progresso por um gráfico visual de funil
 * com formato trapezoidal, gradiente de cores e tooltip interativo.
 */
export const FunnelChart: React.FC<FunnelChartProps> = ({ data }) => {
  const formatDuration = (days: number): string => {
    if (days < 1) return `${Math.round(days * 24)}h`;
    return `${days.toFixed(1)}d`;
  };

  const formatValue = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Transform data for recharts with cumulative values.
  // Each step's value = its own leadsEntered + sum of all subsequent steps.
  // This guarantees the funnel is always wider at the top (earlier stages
  // include everyone who ever passed through them).
  const chartData = data.steps.map((step, index) => {
    const cumulativeValue = data.steps
      .slice(index)
      .reduce((sum, s) => sum + s.leadsEntered, 0);

    return {
      name: step.stepName,
      value: cumulativeValue,
      originalValue: step.leadsEntered,
      leadsInProgress: step.leadsInProgress,
      conversionRate: step.conversionRate,
      avgDurationDays: step.avgDurationDays,
      colorIndex: index,
      fill: FUNNEL_COLORS[index % FUNNEL_COLORS.length],
    };
  });

  // Custom label renderer for funnel steps
  // Shows: step name, lead count, and conversion % badge inside a colored box
  const renderCustomLabel = (props: any) => {
    const { x, y, width, height, value, index } = props;
    const entry = chartData[index];
    if (!entry) return null;

    const centerX = x + width / 2;
    const centerY = y + height / 2;

    // Conversion based on cumulative values (displayed values)
    // First step is always 100%, others = thisValue / prevValue
    const prevCumulativeValue = index > 0 ? chartData[index - 1].value : 0;
    const conversionPct = index === 0
      ? 100
      : prevCumulativeValue > 0
        ? (value / prevCumulativeValue) * 100
        : 0;

    const pctText = `${conversionPct.toFixed(0)}%`;
    const pctBadgeWidth = pctText.length * 7 + 12;

    // Estimate text widths to size the box dynamically
    const nameWidth = entry.name.length * 7; // ~7px per char at fontSize 12
    const valueStr = String(value);
    const valueWidth = valueStr.length * 9.7;  // ~9.7px per char at fontSize 16 bold
    const bottomRowWidth = valueWidth + 4 + pctBadgeWidth; // count + gap + badge
    const contentWidth = Math.max(nameWidth, bottomRowWidth);
    const padX = 16; // horizontal padding
    const padY = 6;  // vertical padding
    const boxWidth = contentWidth + padX * 2;
    const boxHeight = 46;
    const boxX = centerX - boxWidth / 2;
    const boxY = centerY - boxHeight / 2;

    return (
      <g>
        {/* Background box with first step color - overflows narrow steps */}
        <rect
          x={boxX}
          y={boxY}
          width={boxWidth}
          height={boxHeight}
          rx={8}
          fill="rgba(255,255,255,0.94)"
          style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.10))' }}
        />
        {/* Step name */}
        <text
          x={centerX}
          y={boxY + padY + 12}
          textAnchor="middle"
          fill="#1e293b"
          fontSize={12}
          fontWeight={600}
        >
          {entry.name}
        </text>
        {/* Lead count + conversion badge on same line */}
        <text
          x={centerX - pctBadgeWidth / 2 - 4}
          y={boxY + padY + 30}
          textAnchor="end"
          fill="#1e293b"
          fontSize={16}
          fontWeight={800}
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {value}
        </text>
        {/* Conversion badge pill */}
        <rect
          x={centerX - pctBadgeWidth / 2}
          y={boxY + padY + 18}
          width={pctBadgeWidth}
          height={16}
          rx={8}
          fill="#f3e8ff"
        />
        <text
          x={centerX}
          y={boxY + padY + 29.5}
          textAnchor="middle"
          fill="#6b21a8"
          fontSize={10.5}
          fontWeight={700}
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {pctText}
        </text>
      </g>
    );
  };

  const FUNNEL_CHART_HEIGHT = 408;

  return (
    <div
      className="rounded-lg border"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Header */}
      <div className="px-4 py-3 pb-2">
        <div className="flex items-center justify-between">
          <div>
            <h3
              className="text-sm font-semibold"
              style={{ color: 'var(--theme-card-text)' }}
            >
              {data.funnelName}
            </h3>
            <p
              className="text-xs mt-0.5"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{data.totalLeads}</span> leads
              {' · '}
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{data.overallConversionRate.toFixed(1)}%</span> conversão
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-center">
              <p
                className="text-[10px] uppercase tracking-wide"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Valor Total
              </p>
              <p
                className="text-xs font-semibold"
                style={{ color: 'var(--theme-success-color)', fontVariantNumeric: 'tabular-nums' }}
              >
                {formatValue(data.totalValue)}
              </p>
            </div>
            <div className="text-center">
              <p
                className="text-[10px] uppercase tracking-wide"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Ciclo Médio
              </p>
              <p
                className="text-xs font-semibold"
                style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
              >
                {formatDuration(data.avgCycleTimeDays)}
              </p>
            </div>
            <WidgetHeaderIcon icon={'target'} />
          </div>
        </div>
      </div>

      {/* Summary Stats — mockup `.fn-sum`: 2 células com DIVISOR VERTICAL, valor 17px/800 */}
      <div
        className="grid grid-cols-2 px-4 py-2.5 border-t"
        style={{ borderColor: 'var(--theme-card-border)' }}
      >
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 mb-0.5">
            <Icon name="trending-up" className="h-3 w-3" style={{ color: 'var(--theme-success-color)' }} />
            <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--theme-muted-text)' }}>
              Ganhos
            </span>
          </div>
          <p
            className="text-[17px] font-extrabold"
            style={{ color: 'var(--theme-success-color)', fontVariantNumeric: 'tabular-nums' }}
          >
            {data.leadsWon}
          </p>
        </div>
        <div className="text-center" style={{ borderLeft: '1px solid var(--theme-card-border)' }}>
          <div className="flex items-center justify-center gap-1 mb-0.5">
            <Icon name="target" className="h-3 w-3" style={{ color: 'var(--theme-error-color)' }} />
            <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--theme-muted-text)' }}>
              Perdidos
            </span>
          </div>
          <p
            className="text-[17px] font-extrabold"
            style={{ color: 'var(--theme-error-color)', fontVariantNumeric: 'tabular-nums' }}
          >
            {data.leadsLost}
          </p>
        </div>
      </div>

      {/* Funnel Chart */}
      <div
        className="px-2 py-3 border-t funnel-chart-overflow"
        style={{ borderColor: 'var(--theme-card-border)', overflow: 'visible' }}
      >
        <ResponsiveContainer width="100%" height={FUNNEL_CHART_HEIGHT} style={{ overflow: 'visible' }}>
          <RechartsFunnelChart style={{ overflow: 'visible' }}>
            <Tooltip content={<CustomFunnelTooltip />} />
            <Funnel
              dataKey="value"
              data={chartData}
              isAnimationActive
              lastShapeType="rectangle"
            >
              <LabelList
                dataKey="value"
                position="center"
                content={renderCustomLabel}
              />
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.fill}
                  stroke="var(--theme-card-background, #fff)"
                  strokeWidth={2}
                />
              ))}
            </Funnel>
          </RechartsFunnelChart>
        </ResponsiveContainer>
      </div>

    </div>
  );
};

FunnelChart.displayName = 'FunnelChart';
